import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Save, Search } from "@mui/icons-material";

type ValueMetaRow = {
  option_name: string;
  option_value: string;
  description: string | null;
};

const norm = (s: string) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

export default function ValueMetaDialog(props: {
  open: boolean;
  onClose: () => void;

  productId: number;
  optionName: string;
  values: string[];

  valueMeta: ValueMetaRow[];
  onSaveValueMeta: (args: {
    productId: number;
    option_name: string;
    option_value: string;
    description: string | null;
  }) => Promise<void>;
}) {
  const { open, onClose, productId, optionName, values, valueMeta, onSaveValueMeta } = props;

  const [q, setQ] = useState("");
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const metaMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of valueMeta) {
      if (norm(row.option_name) !== norm(optionName)) continue;
      map.set(norm(row.option_value), row.description ?? "");
    }
    return map;
  }, [valueMeta, optionName]);

  const filtered = useMemo(() => {
    const qq = norm(q);
    if (!qq) return values;
    return values.filter(v => norm(v).includes(qq));
  }, [values, q]);

  const getDesc = (val: string) => {
    const k = norm(val);
    return Object.prototype.hasOwnProperty.call(draft, k) ? draft[k] : (metaMap.get(k) ?? "");
  };

  const setDesc = (val: string, next: string) => {
    const k = norm(val);
    setDraft(prev => ({ ...prev, [k]: next }));
  };

  const saveOne = async (val: string) => {
    const k = norm(val);
    const desc = getDesc(val).trim();
    setSaving(prev => ({ ...prev, [k]: true }));
    try {
      await onSaveValueMeta({
        productId,
        option_name: optionName,
        option_value: val,
        description: desc.length ? desc : null,
      });
    } finally {
      setSaving(prev => ({ ...prev, [k]: false }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Descriptions — {optionName || "Option"}</DialogTitle>

      <DialogContent dividers>
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search values..."
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: 520, overflow: "auto" }}>
          {filtered.map((val) => {
            const k = norm(val);
            const busy = !!saving[k];
            return (
              <Box
                key={k}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "220px 1fr 44px",
                  gap: 1,
                  alignItems: "start",
                }}
              >
                <Typography sx={{ mt: 1, fontWeight: 600 }} noWrap title={val}>
                  {val}
                </Typography>

                <TextField
                  value={getDesc(val)}
                  onChange={(e) => setDesc(val, e.target.value)}
                  placeholder="Optional description..."
                  size="small"
                  multiline
                  minRows={2}
                  fullWidth
                />

                <IconButton
                  onClick={() => void saveOne(val)}
                  disabled={busy}
                  color="primary"
                  sx={{ mt: 0.5 }}
                >
                  {busy ? <CircularProgress size={18} /> : <Save fontSize="small" />}
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );
}
